import { createSelector } from '@reduxjs/toolkit'
import { isNil } from 'lodash-es'
import { EntityType, ValidationIssue, TradingEntityAssignmentDetail } from '../../api'
import { createEntityGetterSelector } from '../entitySelectors'
import { GlobalState } from '../../store'
import { compareInsensitive, ParametricSelector } from '../../utils'

export function createTradingEntityAssignmentDetailsSelector<TProps>(
  dealIdSelector: ParametricSelector<{}, TProps, string | null>
): ParametricSelector<GlobalState, TProps, TradingEntityAssignmentDetail[] | null> {
  return createSelector(
    createEntityGetterSelector(EntityType.TRADING_ENTITY),
    createEntityGetterSelector(EntityType.DEAL),
    dealIdSelector,
    (getTradingEntity, getDeal, dealId) => {
      if (isNil(dealId)) {
        return null
      }

      const deal = getDeal(dealId)

      const fundAssignment = deal?.fundAssignment

      if (isNil(fundAssignment)) {
        return null
      }

      return fundAssignment.assignments
        .reduce<TradingEntityAssignmentDetail[]>((acc, assignment) => {
          const tradingEntity = getTradingEntity(assignment.tradingEntityId)

          if (tradingEntity) {
            acc.push({
              tradingEntityAssignment: assignment,
              tradingEntity,
              fieldValidationIssues: buildFieldValidationIssues(assignment.validationIssues)
            })
          }
          return acc
        }, [])
        .sort((a, b) => compareInsensitive(a.tradingEntity.publishedName, b.tradingEntity.publishedName))
    }
  )
}

const buildFieldValidationIssues = (validationIssues: ValidationIssue[] | null) => {
  return validationIssues
    ? validationIssues.reduce(
        (acc, validationIssue) => ({
          ...acc,
          [validationIssue.fieldName]: acc[validationIssue.fieldName]
            ? [...acc[validationIssue.fieldName], validationIssue]
            : [validationIssue]
        }),
        {} as Record<string, ValidationIssue[]>
      )
    : {}
}
